import {useEffect, useState} from "react";
import {
    Box,
    Drawer,
    AppBar,
    Toolbar,
    Typography,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Divider,
    useMediaQuery,
    Badge,
    Chip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import { Link, useLocation } from "react-router-dom";
import { TOOLS, NAV_SECONDARY } from "../util/config";
import { colors } from "../theme/theme";
import { statsApi} from "../api/client";

const DRAWER_WIDTH = 264;

const SidebarContent = () => {
    const location = useLocation();
    const isActive = (path) => location.pathname === path;

    return (
        <Box
            sx={{
                height: "100%",
                bgcolor: colors.sidebarBg,
                color: colors.sidebarText,
                display: "flex",
                flexDirection: "column",
                borderRight: `1px solid ${colors.sidebarBorder}`,
            }}
        >
            {/* Brand */}
            <Box sx={{ px: 3, py: 3, display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                    sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "10px",
                        background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentDark} 100%)`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontFamily: '"Outfit"',
                        fontWeight: 800,
                        fontSize: 18,
                        color: "white",
                        boxShadow: `0 4px 12px ${colors.accent}40`,
                    }}
                >
                    K
                </Box>
                <Box>
                    <Typography
                        sx={{
                            fontFamily: '"Outfit"',
                            fontWeight: 700,
                            fontSize: 15,
                            lineHeight: 1.1,
                            letterSpacing: "-0.01em",
                        }}
                    >
                        KM-BIZZ
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: '"JetBrains Mono", monospace',
                            fontSize: 10,
                            color: colors.sidebarMuted,
                            letterSpacing: "0.12em",
                            textTransform: "uppercase",
                        }}
                    >
                        PDF Studio
                    </Typography>
                </Box>
            </Box>

            <Divider sx={{ borderColor: colors.sidebarBorder }} />

            {/* Tools section */}
            <Box sx={{ px: 2, py: 2, flex: 1, overflowY: "auto" }}>
                <Typography
                    sx={{
                        px: 1.5,
                        fontFamily: '"JetBrains Mono", monospace',
                        fontSize: 10,
                        color: colors.sidebarMuted,
                        letterSpacing: "0.15em",
                        textTransform: "uppercase",
                        mb: 1,
                    }}
                >
                    Tools
                </Typography>
                <List dense disablePadding>
                    {TOOLS.map((tool) => {
                        const Icon = tool.icon;
                        const active = isActive(tool.path);
                        return (
                            <ListItemButton
                                key={tool.id}
                                component={Link}
                                to={tool.path}
                                sx={{
                                    borderRadius: 2,
                                    mb: 0.5,
                                    py: 1,
                                    color: active ? "white" : colors.sidebarText,
                                    bgcolor: active ? colors.sidebarSurface : "transparent",
                                    "&:hover": { bgcolor: colors.sidebarSurface },
                                    position: "relative",
                                    "&::before": active
                                        ? {
                                              content: '""',
                                              position: "absolute",
                                              left: -8,
                                              top: 10,
                                              bottom: 10,
                                              width: 3,
                                              borderRadius: 2,
                                              bgcolor: tool.accent,
                                          }
                                        : {},
                                }}
                            >
                                <ListItemIcon
                                    sx={{
                                        minWidth: 34,
                                        color: active ? tool.accent : colors.sidebarMuted,
                                    }}
                                >
                                    <Icon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText
                                    primary={tool.label}
                                    primaryTypographyProps={{
                                        fontSize: 14,
                                        fontWeight: active ? 600 : 500,
                                    }}
                                />
                            </ListItemButton>
                        );
                    })}
                </List>

                <Typography
                    sx={{
                        px: 1.5,
                        mt: 3,
                        fontFamily: '"JetBrains Mono", monospace',
                        fontSize: 10,
                        color: colors.sidebarMuted,
                        letterSpacing: "0.15em",
                        textTransform: "uppercase",
                        mb: 1,
                    }}
                >
                    Insights
                </Typography>
                <List dense disablePadding>
                    {NAV_SECONDARY.map((item) => {
                        const Icon = item.icon;
                        const active = isActive(item.path);
                        return (
                            <ListItemButton
                                key={item.id}
                                component={Link}
                                to={item.path}
                                sx={{
                                    borderRadius: 2,
                                    mb: 0.5,
                                    py: 1,
                                    color: active ? "white" : colors.sidebarText,
                                    bgcolor: active ? colors.sidebarSurface : "transparent",
                                    "&:hover": { bgcolor: colors.sidebarSurface },
                                }}
                            >
                                <ListItemIcon
                                    sx={{
                                        minWidth: 34,
                                        color: active ? colors.accent : colors.sidebarMuted,
                                    }}
                                >
                                    <Icon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText
                                    primary={item.label}
                                    primaryTypographyProps={{
                                        fontSize: 14,
                                        fontWeight: active ? 600 : 500,
                                    }}
                                />
                            </ListItemButton>
                        );
                    })}
                </List>
            </Box>

            {/* Footer — free badge */}
            <Box sx={{ p: 2, borderTop: `1px solid ${colors.sidebarBorder}` }}>
                <Box
                    sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: colors.sidebarSurface,
                        border: `1px solid ${colors.sidebarBorder}`,
                    }}
                >
                    <Chip
                        label="FREE FOR EVERYONE"
                        size="small"
                        sx={{
                            bgcolor: colors.accent,
                            color: "white",
                            fontFamily: '"JetBrains Mono", monospace',
                            fontSize: 9,
                            fontWeight: 700,
                            letterSpacing: "0.1em",
                            height: 20,
                            mb: 1,
                        }}
                    />
                    <Typography sx={{ fontSize: 12, color: colors.sidebarMuted, lineHeight: 1.5 }}>
                        Open tools for everyone. No login. No paywalls.
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

export const Layout = ({ children }) => {
    const muiTheme = useTheme();
    const isDesktop = useMediaQuery(muiTheme.breakpoints.up("md"));
    const [mobileOpen, setMobileOpen] = useState(false);
    const location = useLocation();
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);


    useEffect(() => {
        Promise.any([
            statsApi.summary(),
            new Promise((resolve, reject) => {
                setTimeout(() => reject(new Error("API timed out")), 10000);
            }),
        ]).then((s) => {
            setSummary(s);
        }).catch(()=>{
            setSummary({ totals: {}, totalExports: 0, totalBytes: 0, last7Days: 0, perTool: {} });
        }).finally(() => setLoading(false))


    }, []);
    const currentTool =
        TOOLS.find((t) => t.path === location.pathname) ||
        NAV_SECONDARY.find((n) => n.path === location.pathname);

    return (
        <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: colors.bg }}>
            {/* Desktop permanent drawer */}
            {isDesktop && (
                <Drawer
                    variant="permanent"
                    sx={{
                        width: DRAWER_WIDTH,
                        flexShrink: 0,
                        "& .MuiDrawer-paper": {
                            width: DRAWER_WIDTH,
                            border: "none",
                            bgcolor: "transparent",
                        },
                    }}
                    open
                >
                    <SidebarContent />
                </Drawer>
            )}

            {/* Mobile temporary drawer */}
            {!isDesktop && (
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={() => setMobileOpen(false)}
                    ModalProps={{ keepMounted: true }}
                    sx={{
                        "& .MuiDrawer-paper": {
                            width: DRAWER_WIDTH,
                            border: "none",
                            bgcolor: "transparent",
                        },
                    }}
                >
                    <SidebarContent />
                </Drawer>
            )}

            {/* Main column */}
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
                <AppBar
                    position="sticky"
                    elevation={0}
                    sx={{
                        bgcolor: colors.surface,
                        color: colors.text,
                        borderBottom: `1px solid ${colors.border}`,
                    }}
                >
                    <Toolbar sx={{ minHeight: 64, gap: 2 }}>
                        {!isDesktop && (
                            <IconButton edge="start" onClick={() => setMobileOpen(true)}>
                                <MenuIcon />
                            </IconButton>
                        )}
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                sx={{
                                    fontFamily: '"JetBrains Mono", monospace',
                                    fontSize: 10,
                                    color: colors.textMuted,
                                    letterSpacing: "0.15em",
                                    textTransform: "uppercase",
                                }}
                            >
                                {currentTool ? "Tool" : "Page"}
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: '"Outfit"',
                                    fontWeight: 700,
                                    fontSize: 18,
                                    letterSpacing: "-0.01em",
                                    lineHeight: 1.2,
                                }}
                            >
                                {currentTool ? currentTool.label : "KM-BIZZ PDF Tools"}
                            </Typography>
                        </Box>
                        {loading  ?
                            null : TOOLS.map((tool) => {
                                const ToolIcon = tool.icon;
                                const total = summary?.perTool[tool.id] || 0;
                                return (
                                    <IconButton key={tool.id} badgeContent={total?.count} size="large"
                                                aria-label="show 4 new mails" color="inherit">
                                        <Badge badgeContent={total?.count} color="error">
                                            <ToolIcon/>
                                        </Badge>
                                    </IconButton>
                                )
                            })
                        }

                    </Toolbar>
                </AppBar>

                <Box component="main" sx={{ flex: 1, p: { xs: 2, md: 4 } }}>
                    {children}
                </Box>

                <Box
                    component="footer"
                    sx={{
                        borderTop: `1px solid ${colors.border}`,
                        bgcolor: colors.surface,
                        px: { xs: 2, md: 4 },
                        py: 3,
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: { xs: "column", md: "row" },
                            alignItems: { xs: "flex-start", md: "center" },
                            gap: { xs: 1.5, md: 3 },
                        }}
                    >
                        {/* Copyright + dev credit */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box
                                sx={{
                                    width: 22, height: 22, borderRadius: "5px",
                                    background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentDark} 100%)`,
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    fontFamily: '"Outfit"', fontWeight: 800, fontSize: 11, color: "white",
                                }}
                            >
                                K
                            </Box>
                            <Typography sx={{ fontSize: 13, color: colors.textMuted }}>
                                Copyright © {new Date().getFullYear()} · Developed by{" "}
                                <Box
                                    component="a"
                                    href="https://km-bizz.com"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                        color: colors.text,
                                        fontWeight: 600,
                                        textDecoration: "none",
                                        borderBottom: `1px solid ${colors.accent}`,
                                        "&:hover": { color: colors.accent },
                                    }}
                                >
                                    KM-BIZZ
                                </Box>
                            </Typography>
                        </Box>

                        <Box sx={{ flex: 1, display: { xs: "none", md: "block" } }} />

                        {/* Links */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 2, md: 3 }, flexWrap: "wrap" }}>
                            <Box
                                component="a"
                                href="https://km-bizz.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    fontFamily: '"JetBrains Mono"',
                                    fontSize: 12,
                                    letterSpacing: "0.08em",
                                    color: colors.textMuted,
                                    textDecoration: "none",
                                    "&:hover": { color: colors.text },
                                }}
                            >
                                km-bizz.com
                            </Box>
                            <Box
                                component="a"
                                href="mailto:contact@km-bizz.com"
                                sx={{
                                    fontFamily: '"JetBrains Mono"',
                                    fontSize: 12,
                                    letterSpacing: "0.08em",
                                    color: colors.textMuted,
                                    textDecoration: "none",
                                    "&:hover": { color: colors.text },
                                }}
                            >
                                contact@km-bizz.com
                            </Box>
                            <Box
                                sx={{
                                    fontFamily: '"JetBrains Mono"',
                                    fontSize: 10,
                                    fontWeight: 700,
                                    letterSpacing: "0.12em",
                                    color: colors.accent,
                                    border: `1px solid ${colors.accent}`,
                                    px: 1, py: 0.25,
                                    borderRadius: 0.5,
                                }}
                            >
                                MADE IN BERLIN
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};
